import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import {
  DashboardChartData,
  GroupedChartRow,
  GroupedChartSeries,
  stackedBarLegendItems,
} from "../../../_models";

export enum BarChartType {
  Vertical = 'VERTICAL',
  Horizontal = 'HORIZONTAL',
  StackedHorizontal = 'STACKED_HORIZONTAL',
}

@Component({
  selector: "bar-chart",
  templateUrl: "./bar-chart.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrls: ["./bar-chart.component.scss"],
})
export class BarChartComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { static: false }) chartContainer: ElementRef;
  @Input("isReport") isReport: boolean = false;
  barChartType = BarChartType;
  @Input("chartType") chartType: BarChartType = BarChartType.Vertical;
  @Input("chartData") chartData: DashboardChartData = [];
  @Input("groupedChartData") groupedChartData: GroupedChartRow[] = [];
  @Input() isFullLength = false;
  customXAxisTicks: number[] = [];
  customYAxisTicks: number[] = [];
  stackedXScaleMax: number = null;
  dynamicView: [number, number] = [500, 340];

  isSmallScreen = false;
  colorScheme = {
    domain: ["#22B8D7"],
  };
  groupedColorScheme = {
    domain: ["#00D67E", "#FF8971", "#FFB347"],
  };

  @HostListener("window:resize", ["$event"])
  onResize(event: Event): void {
    this.updateDynamicView();
  }

  ngOnInit(): void {
    this.updateDynamicView();
    if (this.chartType === BarChartType.Horizontal) {
      this.customXAxisTicks = this.calculateTicks();
    }
    if (this.chartType === BarChartType.Vertical) {
      this.customYAxisTicks = this.calculateTicks();
    }
    if (this.chartType === BarChartType.StackedHorizontal) {
      this.customXAxisTicks = this.calculateStackedTicks();
    }
  }

  ngAfterViewInit(): void {
    this.updateDynamicView();
  }

  updateDynamicView() {
    if (this.isReport) {
      this.dynamicView = [368, 278];
      return;
    }

    const screenWidth = window.innerWidth;
    const width =
      screenWidth <= 768
        ? 300
        : screenWidth <= 991
        ? 400
        : screenWidth <= 1499
        ? 430
        : screenWidth <= 1550
        ? 460
        : screenWidth <= 1750
        ? 520
        : 600;

    this.dynamicView[0] = width;
    if (this.chartContainer && this.chartContainer.nativeElement) {
      const containerWidth = this.chartContainer.nativeElement.clientWidth - 40;
      const height = 340;
      this.dynamicView = [containerWidth, height];
    }

    if (this.chartType === BarChartType.StackedHorizontal) {
      const rowCount = this.groupedChartData.length;
      this.dynamicView = [this.dynamicView[0], this.getStackedHeight(rowCount)];
    }
  }

  // For few rows, scale height to avoid oversized bars; for many rows, grow to fit.
  private getStackedHeight(rowCount: number): number {
    let height = 340;
    if (rowCount <= 3) {
      height = 70 + rowCount * 60;
    } else if (rowCount > 6) {
      height = rowCount * 50;
    }
    return height;
  }

  wrapTicks(label: string): string {
    const screenWidth = window.innerWidth;
    const labelSliceLength =
      screenWidth <= 768
        ? 14
        : screenWidth <= 991
        ? 35
        : screenWidth <= 1233
        ? 14
        : screenWidth <= 1433
        ? 20
        : 25;

    return label.length > labelSliceLength
      ? label.slice(0, labelSliceLength) + "..."
      : label;
  }

  formatKTicks(value: number): string {
    let result: string;
    if (value >= 1000) {
      result = parseFloat((value / 1000).toFixed(1)) + 'k';
    } else {
      result = String(parseFloat(value.toFixed(1)));
    }
    return result;
  }

  getGroupSeries(groupName: string): Array<{ name: string; value: number; color: string; icon: string }> {
    const icons = stackedBarLegendItems.map(item => item.icon);
    let result: Array<{ name: string; value: number; color: string; icon: string }> = [];
    this.groupedChartData.forEach((group) => {
      if (group.name === groupName) {
        result = group.series.map((s: GroupedChartSeries, i: number) => ({
          name: s.name,
          value: s.value,
          color: this.groupedColorScheme.domain[i] || "#ccc",
          icon: icons[i] || "",
        }));
      }
    });
    return result;
  }

  getGroupTotal(groupName: string): number {
    let total = 0;
    this.groupedChartData.forEach((group) => {
      if (group.name === groupName) {
        if (group.requests !== undefined && group.requests !== null) {
          total = group.requests;
        } else {
          group.series.forEach((s: GroupedChartSeries) => {
            total += s.value;
          });
        }
      }
    });
    return total;
  }

  getSeriesPercent(groupName: string, value: number): string {
    const total = this.getGroupTotal(groupName);
    let percent = "0.00";
    if (total > 0) {
      percent = ((value / total) * 100).toFixed(2);
    }
    return percent;
  }

  calculateStackedTicks(): number[] {
    let maxValue = 0;
    this.groupedChartData.forEach((group) => {
      let groupTotal = 0;
      group.series.forEach((s: GroupedChartSeries) => {
        groupTotal += s.value;
      });
      if (groupTotal > maxValue) {
        maxValue = groupTotal;
      }
    });
    const maxTicks = 10; // 11 ticks including 0
    const rawInterval = maxValue / maxTicks;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
    const niceSteps = [1, 2, 5, 10];
    let interval = magnitude;
    for (let i = 0; i < niceSteps.length; i++) {
      const candidate = niceSteps[i] * magnitude;
      if (candidate >= rawInterval) {
        interval = candidate;
        break;
      }
    }
    const maxTick = Math.ceil(maxValue / interval) * interval;
    this.stackedXScaleMax = maxTick;
    const ticks: number[] = [];
    const tickCount = Math.round(maxTick / interval);
    for (let i = 0; i <= tickCount; i++) {
      ticks.push(parseFloat((i * interval).toFixed(10)));
    }
    return ticks;
  }

  calculateTicks(): number[] {
    const maxValue = Math.max(...this.chartData.map((d) => d.value));
    let roundedMaxValue = 0;

    if (maxValue < 5) {
      return Array.from({ length: maxValue + 1 }, (_, index) => index);
    } else if (maxValue < 8) {
      return [0, 2, 4, 6, 8];
    } else if (maxValue < 10) {
      roundedMaxValue = 10;
    } else if (maxValue < 15) {
      roundedMaxValue = 15;
    } else if (maxValue < 20) {
      roundedMaxValue = 20;
    } else if (maxValue < 50) {
      roundedMaxValue = Math.ceil(maxValue / 5) * 5;
    } else if (maxValue < 80) {
      roundedMaxValue = Math.ceil(maxValue / 10) * 10;
    } else if (maxValue < 100) {
      roundedMaxValue = Math.ceil(maxValue / 20) * 20;
    } else if (maxValue < 200) {
      roundedMaxValue = Math.ceil(maxValue / 25) * 25;
    } else if (maxValue < 400) {
      roundedMaxValue = Math.ceil(maxValue / 50) * 50;
    } else if (maxValue < 1000) {
      roundedMaxValue = Math.ceil(maxValue / 100) * 100;
    } else if (maxValue < 1750) {
      roundedMaxValue = Math.ceil(maxValue / 250) * 250;
    } else {
      roundedMaxValue = Math.ceil(maxValue / 500) * 500;
    }

    if (roundedMaxValue <= 10) return [0, 2, 4, 6, 8, 10];
    else if (roundedMaxValue <= 15) return [0, 3, 6, 9, 12, 15];
    else if (roundedMaxValue <= 20) return [0, 4, 8, 12, 16, 20];
    else {
      const intervalSize = Math.ceil(roundedMaxValue / 5 / 5) * 5;
      const numberOfIntervals = Math.ceil(roundedMaxValue / intervalSize); // Maximum of 6 intervals, or less if roundedMaxValue is smaller
      const ticks = [];

      for (let i = 0; i < numberOfIntervals; i++) {
        ticks.push(i * intervalSize);
      }

      if (roundedMaxValue > ticks[ticks.length - 1])
        ticks.push(ticks[ticks.length - 1] + intervalSize); // Add the roundedMaxValue as the last tick

      return ticks;
    }
  }
}
